import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./screens/sidebar";
import AddUsers from "./screens/add-users";
import Inventory from "./screens/inventory";
import Invoices from "./screens/invoices";
import LoginScreen from "./screens/login";
import { AuthProvider, useAuth } from "./auth-context";
import Dashboard from "./screens/dashboard";
import ManageUsers from "./screens/manage-users";
import ConsolidatedInvoiceScreen from "./screens/ConsolidatedInvoiceScreen";
import { createTheme, ThemeProvider, useMediaQuery, Box, Container } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import IngredientsScreen from "./screens/ingredientScreen";
import PurchaseInventory from "./screens/purchaseInventory";
import PurchaseOrder from "./screens/purchaseOrder";
import WasteManagementTab from "./screens/WasteManagementTab";
import ItemTrackingTab from "./screens/ItemTrackingTab";
import MandatoryItemsScreen from "./screens/MandatoryItemsScreen";
import CutoffNotificationsSettings from "./screens/CutoffNotificationsSettings";

// Enhanced theme for Crispy Dosa
const theme = createTheme({
  typography: {
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
  },
  palette: {
    primary: {
      main: "#2E7D32",
      light: "#4CAF50",
      dark: "#1B5E20",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF6B35",
      light: "#FF8A65",
      dark: "#E64A19",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#28A745",
      light: "#4CAF50",
      dark: "#1E7E34",
    },
    warning: {
      main: "#FFC107",
      light: "#FFEB3B",
      dark: "#F57C00",
    },
    error: {
      main: "#DC3545",
      light: "#F44336",
      dark: "#C62828",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(46, 125, 50, 0.05)",
    "0px 4px 8px rgba(46, 125, 50, 0.08)",
    "0px 8px 16px rgba(46, 125, 50, 0.12)",
    "0px 12px 24px rgba(46, 125, 50, 0.15)",
    // ... extend as needed
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          padding: "10px 20px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 8px rgba(46, 125, 50, 0.08)",
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 8px rgba(46, 125, 50, 0.1)",
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return children;
};

const AppRoutes = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userRole } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const getMainContentStyle = () => {
    if (isMobile) {
      return {
        width: "100%",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        paddingTop: "64px", // Space for mobile menu button
      };
    }
    
    const sidebarWidth = sidebarCollapsed ? 72 : 260;
    return {
      // marginLeft: `${sidebarWidth}px`,
      width: `calc(100% - ${sidebarWidth}px)`,
      minHeight: "100vh",
      backgroundColor: theme.palette.background.default,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    };
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar onCollapse={setSidebarCollapsed} />
      <Box component="main" sx={getMainContentStyle()}>
        <Container 
          maxWidth="xl" 
          sx={{ 
            padding: { xs: "12px",  },
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/itemTracking" element={<ItemTrackingTab />} />
            <Route path="/add-users" element={<AddUsers />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/ingredients" element={<IngredientsScreen />} />
            <Route path="/invoices" element={<Invoices />} />
            {userRole === "superadmin" && (
              <Route path="/ConsolidatedInvoiceScreen" element={<ConsolidatedInvoiceScreen />} />
            )}
            <Route path="/purchaseInventory" element={<PurchaseInventory />} />
            <Route path="/purchaseOrder" element={<PurchaseOrder />} />
            <Route path="/MandatoryItemsScreen" element={<MandatoryItemsScreen />} />
            <Route path="/waste-management" element={<WasteManagementTab />} />
            <Route path="/cutoff-notifications" element={<CutoffNotificationsSettings />} />
            {userRole === "superadmin" && (
              <Route path="/manage-users" element={<ManageUsers />} />
            )}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Container>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/" element={<LoginScreen />} />
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <AppRoutes />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
